/**
 * <product-journey> - PDP-drawer med Retraceds produktrejse-kort (Figma 4166:83,
 * labelet "PRODUCT JOURNEY" i koebspanelet lige under accordionen).
 *
 * Indholdet ejes af Retraced: deres <retraced-cards> henter selv kortene fra
 * deres API paa SKU'en i window.retraced (server-renderet af
 * blocks/product-journey.liquid). Vi ejer skallen - trigger, dialog, a11y,
 * tilstande - og roerer ikke deres DOM.
 *
 * Tredjeparts-scriptet (rtcd.me) hentes FOERST ved foerste aabning
 * (ejerbeslutning 2026-09-21). v1 har ellers ingen tredjeparts-CDN'er, og den
 * kunde der aldrig klikker skal ikke betale for dem. Det er muligt fordi
 * Retraceds loader kun injicerer sin cards.js hvis <retraced-cards> ALLEREDE
 * staar i DOM naar loaderen koerer - derfor ligger elementet server-renderet i
 * den lukkede dialog, og loaderen kommer bagefter. Loaderen er engangs (egen
 * guard), saa den maa foerst injiceres naar markup'en staar.
 *
 * Tilstande: kortene lever i en shadow root vi ikke maa kende indmaden af, saa
 * "er der indhold?" maales paa HOEJDEN af vaerten (ResizeObserver) i stedet for
 * paa deres DOM - implementeringsuafhaengigt og bryder ikke naar de udgiver en
 * ny version. Ingen hoejde inden for deadline = produktet har ingen rejse hos
 * Retraced, og draweren siger det i stedet for at staa tom. Maalingen kraever
 * at draweren er AABEN (en lukket <dialog> er display:none = 0px), derfor
 * gentages den ved genaabning indtil den én gang er landet paa 'ready' - og
 * forfra igen naar SKU'en har skiftet siden sidst.
 *
 * Variantskift: variant-picker udsender 'variant:change'; vi slaar SKU'en op i
 * window.retraced.variants, opdaterer initialSku og sender Retraceds eget
 * 'retraced:product-changed', som deres komponent lytter paa. Penge og locale
 * roeres ikke - Retraced viser hverken priser eller oversat chrome fra os.
 *
 * A11y: native <dialog>.showModal() giver focus-trap, Escape og inert baggrund;
 * focus-retur til triggeren + aria oven paa. Tilstanden annonceres i en
 * role=status inde i draweren.
 */

// Retraced henter kortene over nettet efter at scriptet selv er hentet. 8s er
// sat efter deres egen kaede (loader -> cards.js -> API-kald): laengere end et
// realistisk svar, kort nok til at "ingen rejse" ikke foeles som en haengende
// spinner. Deadline'en er hvornaar vi toer SIGE "ingen rejse" - ikke hvornaar
// vi holder op med at kigge: observeren bliver ved, saa et sent svar paa et
// daarligt mobilnet stadig faar lov at rydde beskeden vaek.
const MEASURE_DEADLINE = 8000;

class ProductJourney extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-journey-dialog]');
    this.cards = this.querySelector('retraced-cards');
    this.status = this.querySelector('[data-journey-status]');
    if (!this.dialog || !this.cards) return;
    this.opener = null;
    // injected: loaderen ER lagt i head og skal ikke laegges igen. Nulstilles
    // ved en netvaerksfejl, saa naeste aabning proever forfra.
    this.injected = false;
    // stale: SKU'en er skiftet siden sidste maaling - naeste aabning maaler
    // forfra, ogsaa selvom vi stod paa 'ready'.
    this.stale = false;
    // settled: deadline'en er passeret, saa en hoejde paa 0 betyder nu "ingen
    // rejse" og ikke "ikke landet endnu".
    this.settled = false;

    // Alle handlere er klassefelter (stabile referencer), saa en gentagen
    // connectedCallback - shopify:section:load i editoren - ikke stabler
    // dubletter op: addEventListener dedupliserer paa samme reference.
    this.addEventListener('click', this.#onClick);
    this.dialog.addEventListener('click', this.#onDialogClick);
    this.dialog.addEventListener('close', this.#onDialogClose);
    document.addEventListener('variant:change', this.#onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.#onVariantChange);
    this.#stopWatching();
  }

  open(opener) {
    if (opener) this.opener = opener;
    if (!this.dialog.open) this.dialog.showModal();
    if (!this.injected) {
      this.#inject();
      return;
    }
    // Maal forfra naar vi enten aldrig naaede frem til kort (draweren blev
    // lukket foer svaret, eller SKU'en havde ingen rejse) eller SKU'en har
    // skiftet siden sidst. Ellers staar 'ready' fra forrige variant og daekker
    // over en variant Retraced ikke har nogen rejse til.
    if (this.stale || this.dataset.state !== 'ready') {
      this.stale = false;
      this.#watch();
    }
  }

  #inject() {
    this.injected = true;
    this.#setState('loading');
    const script = document.createElement('script');
    script.src = this.dataset.src;
    script.async = true;
    script.addEventListener('error', () => {
      // Loaderen kom aldrig ned. Slip 'injected', saa naeste aabning proever
      // igen - ellers ville fejlbeskeden (som lover et nyt forsoeg) baade
      // vaere en loegn og 8s senere blive overskrevet af "ingen rejse".
      // Retraceds loader lever i sin egen IIFE, saa en ny <script> koerer den
      // forfra; engangs-guarden er lukket inde i den forrige closure.
      this.injected = false;
      this.#stopWatching();
      this.#setState('error');
    });
    document.head.append(script);
    this.#watch();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  // Klik paa backdrop (event.target er selve dialogen) lukker - samme aftale
  // som cart-drawer og quick-view.
  #onDialogClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  #onDialogClose = () => {
    if (this.opener && document.contains(this.opener)) this.opener.focus();
  };

  #onClick = (event) => {
    if (event.target.closest('[data-journey-open]')) {
      this.open(event.target.closest('[data-journey-open]'));
      return;
    }
    if (event.target.closest('[data-journey-close]')) this.close();
  };

  // Bobler fra <variant-picker>. En picker med data-render-url staar i
  // quick-view paa en ANDEN vare (PLP-promo eller complementary-blokken) og maa
  // ikke flytte drawerens SKU.
  #onVariantChange = (event) => {
    const picker = event.target;
    if (picker instanceof HTMLElement && picker.dataset.renderUrl) return;
    const config = window.retraced;
    if (!config || !Array.isArray(config.variants)) return;
    const id = String(event.detail && event.detail.id);
    const match = config.variants.find((variant) => String(variant.id) === id);
    if (!match || !match.sku || match.sku === config.initialSku) return;
    config.initialSku = match.sku;
    // Retraceds egen kanal: deres komponent genlaeser SKU'en paa dette event.
    window.dispatchEvent(new Event('retraced:product-changed'));
    // Draweren er MODAL, saa et variantskift kan kun ske mens den er lukket
    // (baggrunden er inert) - og en lukket <dialog> er display:none, hvor alt
    // maaler 0px. Der er altsaa intet at maale nu; vi markerer i stedet
    // maalingen som foraeldet, saa naeste aabning starter forfra. Uden det
    // ville en 'ready' fra den forrige SKU staa og skjule at den NYE variant
    // ingen rejse har.
    this.stale = true;
  };

  #watch() {
    this.#stopWatching();
    this.settled = false;
    // Observeren afbrydes IKKE af deadline'en. Svarer Retraced foerst efter
    // 9s, ville kortene ellers rendere under en staaende "ingen rejse"-besked
    // - paa et daarligt mobilnet er det normalfaldet, ikke kanten. Efter
    // deadline'en gaar den ogsaa den anden vej: falder hoejden til 0 (den nye
    // SKU har ingen rejse), skifter beskeden tilbage.
    this.observer = new ResizeObserver(() => {
      if (this.#hasContent()) this.#setState('ready');
      else if (this.settled && this.dialog.open) this.#setState('empty');
    });
    this.observer.observe(this.cards);
    this.deadline = window.setTimeout(() => {
      this.settled = true;
      // En lukket drawer er display:none - alt maaler 0. Lad tilstanden staa
      // og maal igen ved genaabning frem for at paastaa "ingen rejse".
      if (!this.dialog.open) return;
      // Autoritativ maaling, ikke bare det foerste glimt af hoejde: har
      // Retraced kun naaet at tegne en skelet-/wrapperboks, er vi stadig her
      // og retter tilstanden nu.
      this.#setState(this.#hasContent() ? 'ready' : 'empty');
    }, MEASURE_DEADLINE);
  }

  #stopWatching() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    window.clearTimeout(this.deadline);
  }

  #hasContent() {
    return this.cards.getBoundingClientRect().height > 0;
  }

  #setState(state) {
    if (this.dataset.state === state) return;
    this.dataset.state = state;
    if (!this.status) return;
    const message = {
      loading: this.dataset.textLoading,
      empty: this.dataset.textEmpty,
      error: this.dataset.textError,
    }[state];
    this.status.textContent = message || '';
  }
}

if (!customElements.get('product-journey')) {
  customElements.define('product-journey', ProductJourney);
}
